export type BannerType =
  | 'gmail_watch_expired'
  | 'gmail_revoked'
  | 'gmail_not_connected'
  | 'billing_payment_failed'
  | 'gmail_expiring_soon'
  | 'gmail_needs_attention'
  | 'onboarding_incomplete'
  | 'gmail_internal_issue'
  | 'llm_degraded'
  | 'approved_senders_empty'
  | 'sms_paused'
  | 'digest_delivery_failed'
  | 'plan_limit_reached';

export type BannerSeverity = 'critical' | 'warning' | 'info';
export type BannerScope = 'global' | 'page';
export type BannerPageContext = 'forwarding' | 'review';
export type DismissMode = 'none' | 'snooze' | 'forever';

export interface BannerConfig {
  type: BannerType;
  severity: BannerSeverity;
  scope: BannerScope;
  pageContext?: BannerPageContext;
  dismiss: DismissMode;
  defaultSnoozeDays?: number;
  /** Optional: omit for informational banners with no actionable CTA. */
  action?: { label: string; route: string };
  copy: { title: string; description?: string };
}

export const BANNERS: Record<BannerType, BannerConfig> = {
  // ── Errors (1–3): no dismiss, clears when condition resolves ──
  gmail_watch_expired: {
    type: 'gmail_watch_expired',
    severity: 'critical',
    scope: 'global',
    dismiss: 'none',
    action: { label: 'Reconnect Gmail', route: '/settings/forwarding' },
    copy: {
      title: 'Gmail needs to be reconnected',
      description:
        'Your connection was interrupted. Tap below to restore it, it only takes a moment.',
    },
  },
  gmail_revoked: {
    type: 'gmail_revoked',
    severity: 'critical',
    scope: 'global',
    dismiss: 'none',
    action: { label: 'Reconnect Gmail', route: '/settings/forwarding' },
    copy: {
      title: 'Gmail access was revoked',
      description:
        "We've lost permission to read your school emails. Reconnect Gmail to resume processing.",
    },
  },
  gmail_not_connected: {
    type: 'gmail_not_connected',
    severity: 'critical',
    scope: 'global',
    dismiss: 'none',
    action: { label: 'Connect Gmail', route: '/settings/forwarding' },
    copy: {
      title: 'Get your emails flowing',
      description:
        'Connect Gmail once and Yaruta handles the rest, no manual forwarding needed.',
    },
  },
  billing_payment_failed: {
    type: 'billing_payment_failed',
    severity: 'critical',
    scope: 'global',
    dismiss: 'none',
    action: { label: 'Update payment method', route: '/settings/plan' },
    copy: {
      title: 'Payment failed',
      description:
        "We couldn't process your last payment. Update your card to keep your subscription active.",
    },
  },
  // ── Warnings (4–6): session-dismiss with X (snooze 1 day) ──
  gmail_expiring_soon: {
    type: 'gmail_expiring_soon',
    severity: 'warning',
    scope: 'global',
    dismiss: 'snooze',
    defaultSnoozeDays: 1,
    action: { label: 'Refresh connection', route: '/settings/forwarding' },
    copy: {
      title: 'Your Gmail connection expires soon',
      description:
        'Refresh now to avoid interruptions in receiving school emails.',
    },
  },
  gmail_needs_attention: {
    type: 'gmail_needs_attention',
    severity: 'warning',
    scope: 'global',
    dismiss: 'snooze',
    defaultSnoozeDays: 1,
    // Recoverable renewal failure (token still valid). One-tap server
    // refresh — NOT a full Google reconnect. Deliberately warning + snooze,
    // not critical: it must never reintroduce the over-prompting "reconnect"
    // nag we removed for the self-healing expiring_soon case.
    action: { label: 'Refresh connection', route: '/settings/forwarding' },
    copy: {
      title: 'Your Gmail connection needs attention',
      description:
        'We hit a snag keeping your connection active and are retrying. Tap to refresh now — no need to reconnect with Google.',
    },
  },
  onboarding_incomplete: {
    type: 'onboarding_incomplete',
    severity: 'warning',
    scope: 'global',
    dismiss: 'snooze',
    defaultSnoozeDays: 1,
    action: { label: 'Continue setup', route: '/onboarding' },
    copy: {
      title: 'Finish setting up your account',
      description: 'A few quick steps left to unlock everything Yaruta can do.',
    },
  },
  gmail_internal_issue: {
    type: 'gmail_internal_issue',
    severity: 'warning',
    scope: 'global',
    dismiss: 'snooze',
    defaultSnoozeDays: 1,
    // No action — informational only per Figma design 3 (7761:48179).
    copy: {
      title: 'Email processing is paused',
      description:
        "We're experiencing a temporary issue. No action needed — we'll notify you when it's resolved.",
    },
  },
  // ── Informational (7): permanent dismiss for that occurrence ──
  llm_degraded: {
    type: 'llm_degraded',
    severity: 'info',
    scope: 'global',
    dismiss: 'forever',
    copy: {
      title: 'Email processing may be slower than usual',
      description:
        "We're working through a temporary slowdown. Your emails will still be processed.",
    },
  },
  approved_senders_empty: {
    type: 'approved_senders_empty',
    severity: 'info',
    scope: 'page',
    pageContext: 'forwarding',
    dismiss: 'forever',
    action: { label: 'Add senders', route: '/settings/forwarding' },
    copy: {
      title: 'No approved senders yet',
      description:
        'Add the email addresses or domains your school uses so we can route their messages.',
    },
  },
  // Shown after the user replies STOP to our SMS (user_contact_channels
  // .opted_out_at is set). Drives the parent back to Settings to re-enable.
  // (Exact copy flagged for UX sign-off — see the PR's open-questions list.)
  // Title takes the trailing period every other banner's title has; the CTA
  // label reads "Open Settings" (not "Re-enable in Settings") so it doesn't
  // just repeat the description verbatim.
  sms_paused: {
    type: 'sms_paused',
    severity: 'info',
    scope: 'global',
    dismiss: 'forever',
    action: {
      label: 'Open Settings',
      route: '/settings/notifications',
    },
    copy: {
      title: 'Evening digests are paused.',
      description: 'Re-enable in Settings.',
    },
  },
  // Shown on next app open when the caller's latest digest SMS delivery
  // terminally failed (see get_my_latest_digest_failure()). `dismiss:
  // 'forever'` here means "forever for THIS occurrence" — useSystemBanners
  // compares the failed delivery's created_at against the dismissal's
  // dismissed_at so a NEWER failure still resurfaces the banner (YAR-270
  // AC5-6). Same action shape as sms_paused (both point at Settings).
  digest_delivery_failed: {
    type: 'digest_delivery_failed',
    severity: 'info',
    scope: 'global',
    dismiss: 'forever',
    action: {
      label: 'Check your number in Settings',
      route: '/settings/notifications',
    },
    copy: {
      title: "Your evening digest couldn't be delivered last night.",
      description: 'Check your number in Settings.',
    },
  },
  // Review v0 (YAR-555 AC10–12): the month's confirmed emails reached the
  // plan's cap. Rendered by ReviewV0Page itself (it alone knows the count),
  // which fills the description with the used/quota/plan line — the same
  // approved sentence as the plan meter (`quotaExceededMessage`). Clears on
  // its own when a re-read finds the household under the cap again.
  plan_limit_reached: {
    type: 'plan_limit_reached',
    severity: 'warning',
    scope: 'page',
    pageContext: 'review',
    dismiss: 'none',
    action: { label: 'Upgrade plan', route: '/settings/plan' },
    copy: { title: 'Limit reached, upgrade to continue' },
  },
};

export const SEVERITY_ORDER: Record<BannerSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};
